import { useCallback, useEffect, useRef, useState } from "react"

import {
  notebookSharingRequest,
  type NotebookSharing,
  type NotebookSharingScope,
  type SharingAudience,
} from "../utils/notebookSharing"

export function useNotebookSharing(scope: NotebookSharingScope, open: boolean) {
  const [sharing, setSharing] = useState<NotebookSharing | null>(null)
  const [pending, setPending] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const generation = useRef(0)
  const { notebookId, organizationSlug, workspaceId, targetKind } = scope
  useEffect(() => {
    const current = ++generation.current
    setSharing(null)
    setError(null)
    setPending(false)
    setSaving(false)
    if (!open) {
      return
    }
    setPending(true)
    notebookSharingRequest({
      notebookId,
      organizationSlug,
      workspaceId,
      targetKind,
    })
      .then((data) => {
        if (generation.current === current) {
          setSharing(data)
        }
      })
      .catch(() => {
        if (generation.current === current) {
          setError("Could not load sharing. Close and reopen to retry.")
        }
      })
      .finally(() => {
        if (generation.current === current) {
          setPending(false)
        }
      })
    return () => {
      generation.current = current + 1
    }
  }, [open, notebookId, organizationSlug, workspaceId, targetKind])
  const save = useCallback(
    async (audience: SharingAudience) => {
      if (pending || !sharing) {
        return false
      }
      const current = generation.current
      setPending(true)
      setSaving(true)
      setError(null)
      try {
        const updated = await notebookSharingRequest(
          { notebookId, organizationSlug, workspaceId, targetKind },
          audience,
        )
        if (generation.current !== current) {
          return false
        }
        setSharing(updated)
        return true
      } catch {
        if (generation.current === current) {
          setError(
            "Could not save sharing. Your previous setting is still shown; retry or reopen to refresh.",
          )
        }
        return false
      } finally {
        if (generation.current === current) {
          setPending(false)
          setSaving(false)
        }
      }
    },
    [notebookId, organizationSlug, workspaceId, targetKind, pending, sharing],
  )
  return { sharing, pending, saving, error, save }
}
